import type { ImageFit, ResolvedImage } from '@tsagko/label-contracts'

export const DEFAULT_THRESHOLD = 128

/**
 * Output bitmap dimensions for an image fitted into a region (in dots).
 * `^GFA` cannot scale on the printer, so the bitmap IS rendered at these
 * dims. `contain` shrinks to fit preserving aspect; `cover`/`stretch` fill
 * the region (the draw step crops/distorts the source accordingly).
 */
export function computeOutputDots(
  aspect: number,
  regionWidthDots: number,
  regionHeightDots: number,
  fit: ImageFit,
): { widthDots: number; heightDots: number } {
  if (fit === 'cover' || fit === 'stretch') {
    return { widthDots: regionWidthDots, heightDots: regionHeightDots }
  }
  // contain: scale to the limiting axis.
  const regionAspect = regionWidthDots / regionHeightDots
  if (regionAspect > aspect) {
    const heightDots = regionHeightDots
    const widthDots = Math.max(1, Math.round(regionHeightDots * aspect))
    return { widthDots, heightDots }
  }
  const widthDots = regionWidthDots
  const heightDots = Math.max(1, Math.round(regionWidthDots / aspect))
  return { widthDots, heightDots }
}

/** Rec. 601 luma. */
function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b
}

export interface ImageAnalysis {
  isMonochrome: boolean
  otsuThreshold: number
}

/** Monochrome-detection tuning (single-sourced). Biased toward classifying as
 * B&W: a false positive is recoverable via the panel's "Adjust anyway"; a false
 * negative leaves the destructive slider active on a clean logo. */
const MONO_EDGE_BAND = 16 // must stay < 128 so the black/white edge bands never overlap
const MONO_MID_MAX_FRACTION = 0.03

/** 256-bin luminance histogram, same alpha-onto-white luminance as packTo1Bit. */
export function luminanceHistogram(rgba: Uint8ClampedArray): Uint32Array {
  const hist = new Uint32Array(256)
  for (let p = 0; p < rgba.length; p += 4) {
    const a = rgba[p + 3]!
    const lum = a === 0
      ? 255
      : luminance(rgba[p]!, rgba[p + 1]!, rgba[p + 2]!) * (a / 255) + 255 * (1 - a / 255)
    hist[Math.min(255, Math.max(0, Math.round(lum)))]!++
  }
  return hist
}

/**
 * Otsu between-class-variance threshold. `bestT` is the upper bound of the dark
 * class; we return `bestT + 1` so that `lum < threshold` (packTo1Bit's ink test)
 * captures the dark cluster. Clamped to [1,254]; DEFAULT_THRESHOLD for degenerate
 * (single populated bin / empty) histograms.
 */
export function otsuThreshold(hist: Uint32Array): number {
  const total = hist.reduce((s, c) => s + c, 0)
  if (total === 0) return DEFAULT_THRESHOLD
  let sumAll = 0
  for (let t = 0; t < 256; t++) sumAll += t * hist[t]!
  let wB = 0, sumB = 0, maxVar = -1, bestT = -1
  for (let t = 0; t < 256; t++) {
    wB += hist[t]!
    if (wB === 0) continue
    const wF = total - wB
    if (wF === 0) break
    sumB += t * hist[t]!
    const mB = sumB / wB
    const mF = (sumAll - sumB) / wF
    const between = wB * wF * (mB - mF) * (mB - mF)
    // Strict `>` (first-max tie-break) is load-bearing: for a pure 0/255 image
    // every interior split ties, so bestT stays 0 and we return 1 — which packs
    // identically to 128. Switching to `>=` would shift bestT and silently break
    // the B&W↔128 byte-parity guarantee (see the no-regression unit test).
    if (between > maxVar) { maxVar = between; bestT = t }
  }
  if (bestT < 0) return DEFAULT_THRESHOLD
  return Math.min(254, Math.max(1, bestT + 1))
}

/** Already-black-&-white test: mid-tone mass fraction <= MONO_MID_MAX_FRACTION. */
export function isMonochrome(hist: Uint32Array): boolean {
  const total = hist.reduce((s, c) => s + c, 0)
  if (total === 0) return true
  let extreme = 0
  for (let i = 0; i <= MONO_EDGE_BAND; i++) extreme += hist[i]!
  for (let i = 255 - MONO_EDGE_BAND; i < 256; i++) extreme += hist[i]!
  return (total - extreme) / total <= MONO_MID_MAX_FRACTION
}

/** Pure luminance analysis for an RGBA buffer. */
export function analyzeImageLuminance(rgba: Uint8ClampedArray): ImageAnalysis {
  const hist = luminanceHistogram(rgba)
  return { isMonochrome: isMonochrome(hist), otsuThreshold: otsuThreshold(hist) }
}

/**
 * Pack RGBA pixels to 1-bit, row-major, MSB-first, `bytesPerRow =
 * ceil(w/8)`. A pixel is "ink" (bit = 1) when its alpha-weighted luminance
 * is below `threshold`. Transparent pixels (alpha 0) are treated as white.
 */
export function packTo1Bit(
  rgba: Uint8ClampedArray,
  widthDots: number,
  heightDots: number,
  threshold: number,
): Uint8Array {
  const bytesPerRow = Math.ceil(widthDots / 8)
  const out = new Uint8Array(bytesPerRow * heightDots)
  for (let y = 0; y < heightDots; y++) {
    for (let x = 0; x < widthDots; x++) {
      const p = (y * widthDots + x) * 4
      const a = rgba[p + 3]!
      const lum = a === 0
        ? 255
        : luminance(rgba[p]!, rgba[p + 1]!, rgba[p + 2]!) * (a / 255) + 255 * (1 - a / 255)
      if (lum < threshold) {
        const byteIndex = y * bytesPerRow + (x >> 3)
        out[byteIndex]! |= 0x80 >> (x & 7)
      }
    }
  }
  return out
}

/** Inverse of `packTo1Bit` for canvas display: 1 -> opaque black, 0 -> opaque white. */
export function unpack1BitToRgba(
  packed: Uint8Array,
  widthDots: number,
  heightDots: number,
): Uint8ClampedArray {
  const bytesPerRow = Math.ceil(widthDots / 8)
  const out = new Uint8ClampedArray(widthDots * heightDots * 4)
  for (let y = 0; y < heightDots; y++) {
    for (let x = 0; x < widthDots; x++) {
      const bit = (packed[y * bytesPerRow + (x >> 3)]! >> (7 - (x & 7))) & 1
      const v = bit ? 0 : 255
      const p = (y * widthDots + x) * 4
      out[p] = v; out[p + 1] = v; out[p + 2] = v; out[p + 3] = 255
    }
  }
  return out
}

/**
 * Decode encoded image bytes -> an ImageBitmap, exposing natural aspect.
 * Canvas glue (not unit-tested; exercised via the adapter + manual run).
 */
export async function decodeImage(
  bytes: Uint8Array,
  mimeType: string,
): Promise<{ bitmap: ImageBitmap; aspect: number }> {
  const blob = new Blob([bytes as BlobPart], { type: mimeType })
  const bitmap = await createImageBitmap(blob)
  return { bitmap, aspect: bitmap.width / bitmap.height }
}

/** Source-native luminance analysis from a decoded bitmap. Canvas glue. */
export function analyzeBitmap(bitmap: ImageBitmap): ImageAnalysis {
  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, 0, 0)
  const { data } = ctx.getImageData(0, 0, bitmap.width, bitmap.height)
  return analyzeImageLuminance(data)
}

/**
 * Full client-side rasterization: decode -> fit to region -> draw -> threshold
 * -> 1-bit pack. Returns a ResolvedImage whose buffer is exactly
 * widthDots × heightDots. Canvas glue.
 */
export async function rasterizeImage(
  bytes: Uint8Array,
  mimeType: string,
  regionWidthDots: number,
  regionHeightDots: number,
  fit: ImageFit,
  threshold?: number,
): Promise<ResolvedImage> {
  const { bitmap, aspect } = await decodeImage(bytes, mimeType)
  const cutoff = threshold ?? analyzeBitmap(bitmap).otsuThreshold
  const { widthDots, heightDots } = computeOutputDots(
    aspect, regionWidthDots, regionHeightDots, fit,
  )
  const canvas = document.createElement('canvas')
  canvas.width = widthDots
  canvas.height = heightDots
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, widthDots, heightDots)
  if (fit === 'cover') {
    const scale = Math.max(widthDots / bitmap.width, heightDots / bitmap.height)
    const dw = bitmap.width * scale
    const dh = bitmap.height * scale
    ctx.drawImage(bitmap, (widthDots - dw) / 2, (heightDots - dh) / 2, dw, dh)
  } else {
    ctx.drawImage(bitmap, 0, 0, widthDots, heightDots)
  }
  const { data } = ctx.getImageData(0, 0, widthDots, heightDots)
  const buffer = packTo1Bit(data, widthDots, heightDots, cutoff)
  return { buffer, widthDots, heightDots }
}
